// src/features/inspections/InspectionsList.jsx - CORREGIDO CON FILTROS SIN ACENTOS Y WARNINGS ARREGLADOS
import React, { useEffect, useState, useMemo, useCallback } from 'react';
import { Container, Button, Card } from 'react-bootstrap';
import { useNavigate } from 'react-router-dom';
import { FaClipboardCheck, FaUser, FaTools, FaUserTie, FaTags } from 'react-icons/fa';
import TableWrapper from '../../components/TableWrapper';
import InspectionsService from './InspectionsService';
import CustomSnackbar from '../../components/CustomSnackbar';
import ExportCsvButton from '../../components/ExportCsvButton';
import FilterSelect from '../../components/FilterSelect';
import { useColumnFilter } from '../../hooks/useColumnFilter';
import { TIPOS_INSPECCIONES, FORMATO_FECHA } from '../../utils/constants';
import dayjs from 'dayjs';

function InspectionsList() {
  const [inspections, setInspections] = useState([]);
  const [filteredInspections, setFilteredInspections] = useState([]);
  const [loading, setLoading] = useState(true);
  const [snackbar, setSnackbar] = useState({ open: false, type: '', message: '' });
  const [gridApi, setGridApi] = useState(null);
  const navigate = useNavigate();

  // ✅ FUNCIÓN PARA NORMALIZAR TEXTO REMOVIENDO ACENTOS/TILDES
  const normalizeText = useCallback((text) => {
    if (!text || typeof text !== 'string') return '';
    return text
      .normalize('NFD') // Descompone caracteres con acentos
      .replace(/[\u0300-\u036f]/g, '') // Remueve marcas diacríticas (acentos)
      .toLowerCase()
      .trim();
  }, []);

  // Función para obtener descripción de tipo
  const getTipoDescripcion = useCallback((id) => {
    const tipo = TIPOS_INSPECCIONES.find(t => t.id === id);
    return tipo ? tipo.descripcion : id;
  }, []);

  // Value getters para campos complejos
  const typeValueGetter = useCallback((item) => {
    return item.typeLabel || getTipoDescripcion(item.type);
  }, [getTipoDescripcion]);

  const toolValueGetter = useCallback((item) => {
    return item.toolName || '';
  }, []);

  // ✅ CONFIGURAR FILTROS PERSISTENTES (sin las variables no utilizadas)
  const { 
    filterOptions: typeOptions, 
    activeFilters: activeTypes,
    handleOptionsUpdate: handleTypeOptionsUpdate,
    handleFiltersChange: handleTypeChange,
    filterData: filterByType
  } = useColumnFilter(inspections, 'type', typeValueGetter, 'inspections');

  const { 
    filterOptions: responsableOptions, 
    activeFilters: activeResponsables,
    handleOptionsUpdate: handleResponsableOptionsUpdate,
    handleFiltersChange: handleResponsableChange,
    filterData: filterByResponsable
  } = useColumnFilter(inspections, 'responsable', null, 'inspections');

  const { 
    filterOptions: personOptions, 
    activeFilters: activePersons,
    handleOptionsUpdate: handlePersonOptionsUpdate,
    handleFiltersChange: handlePersonChange,
    filterData: filterByPerson
  } = useColumnFilter(inspections, 'personName', null, 'inspections');

  const { 
    filterOptions: toolOptions, 
    activeFilters: activeTools,
    handleOptionsUpdate: handleToolOptionsUpdate,
    handleFiltersChange: handleToolChange,
    filterData: filterByTool
  } = useColumnFilter(inspections, 'toolName', toolValueGetter, 'inspections');

  const loadInspections = useCallback(async () => {
    try {
      setLoading(true);
      const data = await InspectionsService.getAllInspections();
      const enriched = data.map(item => {
        const person = item.person || item.assignedTool?.person;
        return {
          ...item,
          typeLabel: getTipoDescripcion(item.type),
          personName: person
            ? `${(person.firstName || '').trim()} ${(person.surname || '').trim()}`
            : '',
          toolName: item.assignedTool?.tool?.name || ''
        };
      });
      setInspections(enriched);
      setFilteredInspections(enriched);
    } catch (error) {
      console.error('Error al cargar inspecciones:', error);
      setSnackbar({
        open: true,
        type: 'error',
        message: 'Error al cargar inspecciones'
      });
    } finally {
      setLoading(false);
    }
  }, [getTipoDescripcion]);

  // Aplicar filtros cuando cambian los filtros activos o los datos
  useEffect(() => {
    if (!inspections || inspections.length === 0) return;
    
    // Aplicar filtros en cadena
    let result = [...inspections];
    
    if (activeTypes && activeTypes.length > 0) {
      result = filterByType(result);
    }
    
    if (activeResponsables && activeResponsables.length > 0) {
      result = filterByResponsable(result);
    }
    
    if (activePersons && activePersons.length > 0) {
      result = filterByPerson(result);
    }
    
    if (activeTools && activeTools.length > 0) {
      result = filterByTool(result);
    }
    
    // Verificar si el resultado ha cambiado para evitar actualizaciones innecesarias
    const resultIds = result.map(item => item.id).sort().join(',');
    const currentIds = filteredInspections.map(item => item.id).sort().join(',');
    
    if (resultIds !== currentIds) {
      setFilteredInspections(result);
    }
  }, [
    inspections, 
    activeTypes, 
    activeResponsables, 
    activePersons, 
    activeTools,
    filterByType,
    filterByResponsable,
    filterByPerson,
    filterByTool,
    filteredInspections
  ]);

  useEffect(() => {
    loadInspections();
  }, [loadInspections]);

  const handleView = useCallback((inspection) => {
    navigate(`/inspections/${inspection.id}`);
  }, [navigate]);

  const handleEdit = useCallback((inspection) => {
    navigate(`/inspections/${inspection.id}/edit`);
  }, [navigate]);

  const handleDelete = useCallback(async (inspection) => {
    if (window.confirm('¿Está seguro de eliminar esta inspección?')) {
      try {
        await InspectionsService.deleteInspection(inspection.id);
        setSnackbar({
          open: true,
          type: 'success',
          message: 'Inspección eliminada correctamente'
        });
        loadInspections();
      } catch (error) {
        console.error('Error al eliminar inspección:', error);
        setSnackbar({
          open: true,
          type: 'error',
          message: 'Error al eliminar inspección'
        });
      }
    }
  }, [loadInspections]);

  const handleAdd = useCallback(() => {
    navigate('/inspections/add');
  }, [navigate]);

  const handleCloseSnackbar = useCallback(() => {
    setSnackbar(prev => ({ ...prev, open: false }));
  }, []);

  // ✅ CONFIGURACIÓN MEJORADA PARA FILTROS DE TEXTO SIN ACENTOS
  const textFilterParams = useMemo(() => ({
    filterOptions: ['contains', 'notContains', 'equals', 'startsWith', 'endsWith'],
    debounceMs: 200,
    suppressAndOrCondition: true,
    // ✅ Función personalizada para comparar texto ignorando acentos
    textMatcher: ({ filterOption, value, filterText }) => {
      if (!filterText) return true;
      
      const normalizedValue = normalizeText(value);
      const normalizedFilter = normalizeText(filterText);
      
      switch (filterOption) {
        case 'contains':
          return normalizedValue.includes(normalizedFilter);
        case 'notContains':
          return !normalizedValue.includes(normalizedFilter);
        case 'equals':
          return normalizedValue === normalizedFilter;
        case 'notEqual':
          return normalizedValue !== normalizedFilter;
        case 'startsWith':
          return normalizedValue.startsWith(normalizedFilter);
        case 'endsWith':
          return normalizedValue.endsWith(normalizedFilter);
        default:
          return normalizedValue.includes(normalizedFilter);
      }
    }
  }), [normalizeText]);

  // ✅ CONFIGURACIÓN ESPECÍFICA PARA CAMPOS DE FECHA (sin normalización)
  const dateFilterParams = useMemo(() => ({
    debounceMs: 200
  }), []);

  // ✅ DEFINICIÓN DE COLUMNAS CON FILTROS MEJORADOS
  const columnDefs = useMemo(() => [
    {
      headerName: 'Tipo',
      field: 'typeLabel',
      flex: 1,
      filter: 'agTextColumnFilter',
      floatingFilter: true,
      filterParams: textFilterParams // ✅ Usa filtro sin acentos
    },
    {
      headerName: 'Fecha',
      field: 'date',
      flex: 1,
      valueFormatter: params => params.value ? dayjs(params.value).format(FORMATO_FECHA) : '',
      filter: 'agTextColumnFilter',
      filterValueGetter: params => {  
        return params.data.date ? dayjs(params.data.date).format(FORMATO_FECHA) : '';
      },
      floatingFilter: true,
      filterParams: dateFilterParams // ✅ Configuración específica para fechas
    },
    {
      headerName: 'Responsable',
      field: 'responsable',
      flex: 1,
      filter: 'agTextColumnFilter',
      floatingFilter: true,
      filterParams: textFilterParams // ✅ Usa filtro sin acentos para nombres
    },
    {
      headerName: 'Persona',
      field: 'personName',
      flex: 1,
      filter: 'agTextColumnFilter',
      floatingFilter: true,
      filterParams: textFilterParams // ✅ Usa filtro sin acentos para nombres
    },
    {
      headerName: 'Herramienta',
      field: 'toolName',
      flex: 1,
      filter: 'agTextColumnFilter',
      floatingFilter: true,
      filterParams: textFilterParams // ✅ Usa filtro sin acentos
    },
    {
      headerName: 'Observaciones',
      field: 'observations',
      flex: 2,
      filter: 'agTextColumnFilter',
      floatingFilter: true,
      filterParams: textFilterParams, // ✅ Usa filtro sin acentos
      cellStyle: { 
        overflow: 'hidden', 
        textOverflow: 'ellipsis', 
        whiteSpace: 'nowrap' 
      },
      tooltipField: 'observations' // AG Grid muestra el contenido completo al pasar el mouse
    }
  ], [textFilterParams, dateFilterParams]);

  // Estilo personalizado para forzar esquinas redondeadas
  const customTableStyle = {
    borderRadius: '8px',
    overflow: 'hidden'
  };

  // Renderizado memoizado para mejorar rendimiento
  const renderContent = useMemo(() => {
    if (loading && filteredInspections.length === 0) {
      return (
        <div className="text-center my-5">
          <div className="spinner-border text-primary" role="status">
            <span className="visually-hidden">Cargando...</span>
          </div>
          <p className="mt-2">Cargando datos...</p>
        </div>
      );
    }

    return (
      <TableWrapper
        columnDefs={columnDefs}
        rowData={filteredInspections}
        tableType="inspecciones"
        onGridReady={(e) => setGridApi(e.api)}
        onView={handleView}
        onEdit={handleEdit}
        onDelete={handleDelete}
        style={customTableStyle}
        customGridOptions={{
          domLayout: 'autoHeight',
          floatingFilter: true,
          headerHeight: 40,
          pagination: true,
          paginationPageSize: 100,
          defaultColDef: {
            flex: 1,
            minWidth: 100,
            resizable: true,
            sortable: true,
            filter: 'agTextColumnFilter',
            floatingFilter: true
          }
        }}
      />
    );
  }, [loading, filteredInspections, columnDefs, handleView, handleEdit, handleDelete, customTableStyle]);

  return (
    <Container fluid style={{ padding: 0 }}>
      <Card className="rounded-card-custom" style={{ overflow: 'hidden', borderRadius: '8px' }}>
        <Card.Header className="bg-primary text-white text-center">
          <h5 className="mb-0">Inspecciones</h5>
        </Card.Header>

        {/* Barra de filtros y acción */}
        <div className="d-flex justify-content-between align-items-center p-2 bg-light border-bottom">
          {/* Filtros dropdown */}
          <div className="d-flex gap-2">
            <FilterSelect
              options={typeOptions}
              activeFilters={activeTypes}
              onFiltersChange={handleTypeChange}
              onOptionsUpdate={handleTypeOptionsUpdate}
              title="Filtrar por tipo"
              filterKey="inspections-types"
              icon={<FaTags />}
            />
            
            <FilterSelect
              options={responsableOptions}
              activeFilters={activeResponsables}
              onFiltersChange={handleResponsableChange}
              onOptionsUpdate={handleResponsableOptionsUpdate}
              title="Filtrar por responsable"
              filterKey="inspections-responsables"
              icon={<FaUserTie />}
            />

            <FilterSelect
              options={personOptions}
              activeFilters={activePersons}
              onFiltersChange={handlePersonChange}
              onOptionsUpdate={handlePersonOptionsUpdate}
              title="Filtrar por persona"
              filterKey="inspections-persons"
              icon={<FaUser />}
            />

            <FilterSelect
              options={toolOptions}
              activeFilters={activeTools}
              onFiltersChange={handleToolChange}
              onOptionsUpdate={handleToolOptionsUpdate}
              title="Filtrar por herramienta"
              filterKey="inspections-tools"
              icon={<FaTools />}
            />
          </div>
          
          {/* Botones de acción */}
          <div className="d-flex gap-2 align-items-center">
            <ExportCsvButton gridApi={gridApi} moduleName="inspecciones" />
            <Button
              variant="primary"
              onClick={handleAdd}
              className="d-flex align-items-center"
            >
              <FaClipboardCheck className="me-1" /> Agregar Inspección
            </Button>
          </div>
        </div>

        <Card.Body className="p-0">
          {renderContent}
        </Card.Body>
      </Card>

      <CustomSnackbar
        open={snackbar.open}
        type={snackbar.type}
        message={snackbar.message}
        onClose={handleCloseSnackbar}
      />

      {/* Estilos CSS inline específicos para este componente */}
      <style jsx="true">{`
        .rounded-card-custom .ag-root-wrapper {
          border-radius: 8px !important;
        }
        .rounded-card-custom .ag-header {
          border-top-left-radius: 8px !important;
          border-top-right-radius: 8px !important;
        }
        .rounded-card-custom .ag-paging-panel {
          border-bottom-left-radius: 8px !important;
          border-bottom-right-radius: 8px !important;
        }
      `}</style>
    </Container>
  );
}

export default InspectionsList;